/*
 * Copyright (c) 2026 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, June 16th 2026, 12:14:29 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { STATUS_CODES } from "lib/api/types";
import { useWorkflowContext } from "service-builder/providers";
import { useDisableTaskTemplate } from "service-builder/services";
import { ActionDialog, OSActionButton, OSLoadingButton, useToggleState } from "ui-widgets";

interface DeactivateTaskTemplateComponentProps {
  openDisable: boolean;
  handleCloseDisable: () => void;
}

export const DeactivateTaskTemplateComponent = ({
  openDisable,
  handleCloseDisable,
}: DeactivateTaskTemplateComponentProps) => {
  const {
    taskTemplateListContext: { currentMutationItem: taskTemplateToDisable, updateList },
  } = useWorkflowContext();

  const { handleClose: handleCloseError, handleOpen: handleOpenError, open: openError } = useToggleState();
  const {
    disableTaskTemplate,
    isMakingAPIRequest: isDisabling,
    isAPIError,
    apiErrors,
    httpResponseStatusCode,
  } = useDisableTaskTemplate();

  React.useEffect(() => {
    if (isAPIError) handleOpenError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAPIError]);

  return taskTemplateToDisable ? (
    <>
      <ActionDialog
        severity="error"
        title={`Deactivate ${taskTemplateToDisable.title}?`}
        open={openDisable}
        onClose={handleCloseDisable}
        renderActions={({ handleClose }) => (
          <>
            <OSActionButton onClick={handleClose} variant="text" size="large">
              Cancel
            </OSActionButton>
            <OSLoadingButton
              variant="contained"
              size="large"
              isLoading={isDisabling}
              onClick={() => {
                taskTemplateToDisable?.id &&
                  disableTaskTemplate(taskTemplateToDisable.id, {
                    onDisable: (taskTemplate) => {
                      updateList(taskTemplate);
                      handleCloseDisable();
                    },
                  });
              }}
            >
              Deactivate
            </OSLoadingButton>
          </>
        )}
        dialogMessage={<>Are you sure you want to deactivate {taskTemplateToDisable.title}</>}
      />
      {isAPIError ? (
        <ActionDialog
          severity="error"
          title={`Cannot deactivate ${taskTemplateToDisable.title}`}
          open={openError}
          onClose={handleCloseError}
          renderActions={({ handleClose }) => (
            <>
              <OSActionButton onClick={handleClose} variant="text" size="large">
                Ok
              </OSActionButton>
            </>
          )}
          dialogMessage={
            httpResponseStatusCode >= STATUS_CODES.ServerError
              ? `An unexpected error occured when trying to disable ${taskTemplateToDisable.title}. Kindly contact us if this issue persists.`
              : apiErrors?.[0]
          }
        />
      ) : null}
    </>
  ) : null;
};
